import type { GroupByQuery } from '@/api/generated/model';
import { METHODOLOGY_DIMENSIONS } from '@/domains/pages/ai-proficiency/constants';
import { AIFI_DIMENSION_VARIABLE_BY_KEY } from '@/domains/pages/ai-proficiency/utils/aifi-aggregates-api';

export type MethodologyDimension = {
  key: string;
  label: string;
  description: string;
  weight: number;
  color: string;
  score: number;
  points: number;
};

function readAggregateScore(row: GroupByQuery, variable: string): number | null {
  const direct = (row as unknown as Record<string, unknown>)[variable];
  if (typeof direct === 'number') return direct;

  const fromCustom = row.custom_variables?.[variable];
  if (typeof fromCustom === 'number') return fromCustom;

  return null;
}

export function clampMethodologyScore(score: number): number {
  return Math.max(0, Math.min(100, score));
}

export function computeDimensionPoints(score: number, weight: number): number {
  return (clampMethodologyScore(score) * weight) / 100;
}

export function mapMethodologyDimensionsFromAggregates(row: GroupByQuery | undefined): MethodologyDimension[] | null {
  if (!row) return null;

  const dimensions = METHODOLOGY_DIMENSIONS.map((dimension) => {
    const variable = AIFI_DIMENSION_VARIABLE_BY_KEY[dimension.key];
    const score = readAggregateScore(row, variable);
    if (score == null) return null;

    const clampedScore = clampMethodologyScore(score);

    return {
      key: dimension.key,
      label: dimension.label,
      description: dimension.description,
      weight: dimension.weight,
      color: dimension.color,
      score: clampedScore,
      points: computeDimensionPoints(clampedScore, dimension.weight),
    };
  });

  if (dimensions.some((dimension) => dimension == null)) return null;
  return dimensions as MethodologyDimension[];
}

/** Synthetic breakdown until dimension aggregates are available. */
export function buildSyntheticMethodologyDimensions(orgScore: number | null): MethodologyDimension[] {
  const baseScore = orgScore ?? 55.9;
  const offsets = [3.1, 1.9, -0.9, -3.9, -2.9, -7.9];

  return METHODOLOGY_DIMENSIONS.map((dimension, index) => {
    const score = clampMethodologyScore(baseScore + offsets[index]);

    return {
      key: dimension.key,
      label: dimension.label,
      description: dimension.description,
      weight: dimension.weight,
      color: dimension.color,
      score,
      points: computeDimensionPoints(score, dimension.weight),
    };
  });
}

export function resolveMethodologyDimensions(
  aggregateRow: GroupByQuery | undefined,
): MethodologyDimension[] | null {
  return mapMethodologyDimensionsFromAggregates(aggregateRow);
}

export function methodologyTotal(dimensions: MethodologyDimension[]): number {
  return dimensions.reduce((sum, dimension) => sum + dimension.points, 0);
}
